import { keepPreviousData, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { StepOutputContracts } from '@/types/automationFields.ts';
import { OUTPUT_CONTRACTS_KEY } from '@/utils/queryKeys.ts';
import { get_output_contracts_url } from '@/utils/urls.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import useRetrieveEditorLocalSubflowContext from '@/hooks/api/automations/useRetrieveEditorLocalSubflowContext.ts';

const useRetrieveOutputContracts = (
  scopeOverride?: 'root' | 'local_subflow',
): UseQueryResult<StepOutputContracts, AxiosError> => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const { data: latestVersion } = useRetrieveLatestAutomationVersion();
  const latestVersionId = latestVersion?.id;
  const { target } = useActiveCompositionTarget();
  const scope = scopeOverride ?? target.scope_kind;
  const subflowId = scope === 'local_subflow' && target.scope_kind === 'local_subflow' ? target.subflow_id : undefined;
  const localContext = useRetrieveEditorLocalSubflowContext(subflowId);

  const rootQuery = useQuery<StepOutputContracts, AxiosError>({
    queryKey: [...OUTPUT_CONTRACTS_KEY(automationId, latestVersionId || ''), scope],
    queryFn: async () => {
      const response = await apiClient.get(get_output_contracts_url(latestVersionId));
      return response.data;
    },
    enabled: !!automationId && !!latestVersionId && scope === 'root',
    placeholderData: keepPreviousData,
  });

  if (scope === 'local_subflow') {
    return {
      ...localContext,
      data: localContext.data ? {
        step_output_contracts: localContext.data.step_output_contracts,
        upstream_output_contracts: localContext.data.upstream_output_contracts,
        children_output_contracts: localContext.data.children_output_contracts,
        input_availability_policies: localContext.data.input_availability_policies,
      } : undefined,
    } as UseQueryResult<StepOutputContracts, AxiosError>;
  }

  return rootQuery;
};

export default useRetrieveOutputContracts;
